/**
 * How it works — methodology docs explaining the scoring approach,
 * AI pipeline, bias audit, and architecture in recruiter-friendly language.
 *
 * IMPORTANT: this page is public-facing. It should build trust and credibility
 * but NOT reveal implementation specifics (model names, exact weights,
 * rescaling bands, thresholds, transfer matrix values). Show WHAT we do,
 * not HOW to replicate it.
 */

import {
  Brain,
  FileSearch,
  GitCompare,
  Layers,
  Scale,
  Shield,
  Sparkles,
  Target,
  Zap,
} from "lucide-react";

import { BENCHMARK_SUMMARY } from "@/data/benchmark";

export default function Docs() {
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-12 pb-24">
      <div className="text-center mb-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-4">
          How TalentAlign Scores
        </h1>
        <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
          Seven independent scoring dimensions, proprietary fusion weights,
          validated against a labeled benchmark, and grounded in bullet-level
          evidence. No black boxes — every score traces back to specific
          resume bullets and JD requirements.
        </p>
      </div>

      {/* Pipeline overview */}
      <div className="ta-card p-6 mb-8">
        <h2 className="text-lg font-bold text-slate-100 mb-4 flex items-center gap-2">
          <Layers className="w-5 h-5 text-blue-400" />
          The Scoring Pipeline
        </h2>
        <div className="grid sm:grid-cols-4 gap-4">
          <PipelineStep
            num="1"
            title="Parse"
            desc="Split resume and JD into sections, extract bullets, identify headings."
          />
          <PipelineStep
            num="2"
            title="Score"
            desc="Run 7 independent scoring dimensions in parallel. Each produces a normalized signal."
          />
          <PipelineStep
            num="3"
            title="Fuse"
            desc="Proprietary weighted fusion produces an overall 0-100 score and a verdict."
          />
          <PipelineStep
            num="4"
            title="Explain"
            desc="AI extracts bullet-level evidence: strengths, gaps, and transferable skills."
          />
        </div>
      </div>

      {/* The 7 signals */}
      <h2 className="text-xl font-bold text-slate-100 mb-6">
        Seven Scoring Dimensions
      </h2>

      <div className="space-y-4 mb-12">
        <SignalCard
          icon={Brain}
          name="Semantic Similarity"
          color="blue"
          description="Measures overall meaning alignment between the resume and JD using neural document embeddings. Captures cases where different words describe the same skills — 'built microservices' matches 'designed distributed systems' even with zero keyword overlap."
        />
        <SignalCard
          icon={FileSearch}
          name="Keyword Overlap"
          color="purple"
          description="Linguistic-aware keyword matching that handles word variations (e.g., 'engineering' matches 'engineer'). Multi-word technical phrases like 'machine learning' or 'data pipeline' receive additional weight because they're more specific than individual terms."
        />
        <SignalCard
          icon={Target}
          name="Bullet Alignment"
          color="emerald"
          description="Matches individual resume accomplishments to specific JD requirements using semantic similarity. Each JD requirement is paired with its closest resume bullet, measuring how completely the candidate covers what the role actually needs."
        />
        <SignalCard
          icon={GitCompare}
          name="Section Alignment"
          color="amber"
          description="Compares resume sections (Skills, Experience, Projects, Education) against the JD requirements with section-specific importance weighting. Technical sections contribute more than peripheral ones."
        />
        <SignalCard
          icon={Zap}
          name="Title Family"
          color="sky"
          description="Classifies both the resume and JD into role families (engineering, data science, design, product, management, and more) and scores their proximity. Prevents false credit from shared seniority words — 'Senior PM' and 'Senior Engineer' land in different families."
        />
        <SignalCard
          icon={Scale}
          name="Seniority Band"
          color="violet"
          description="Extracts experience level from both documents — using employment date spans, explicit statements, and JD requirements — then checks whether the candidate's seniority aligns with what the role demands."
        />
        <SignalCard
          icon={Sparkles}
          name="Field / Domain"
          color="rose"
          description="Identifies the industry domain of both documents and scores cross-domain transferability. A clinical researcher applying for a health data role gets partial credit for transferable statistical skills, while a PM applying for a backend role does not."
        />
      </div>

      {/* Two-stage retrieval */}
      <div className="ta-card p-6 mb-8">
        <h2 className="text-lg font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Layers className="w-5 h-5 text-blue-400" />
          Two-Stage Retrieval
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          For bulk ranking, TalentAlign uses a two-stage retrieval architecture
          inspired by production search systems:
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="rounded-lg bg-slate-900/50 border border-slate-800/60 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-blue-400 mb-2">
              Stage 1: Fast screening
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              A lightweight neural model embeds each document independently,
              enabling all N candidates to be scored in parallel. Embeddings
              are cached so repeat queries are instant.
            </p>
          </div>
          <div className="rounded-lg bg-slate-900/50 border border-slate-800/60 p-4">
            <div className="text-xs uppercase tracking-wider font-semibold text-purple-400 mb-2">
              Stage 2: Precision reranking
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              A cross-encoder reads the resume and JD as a single joint input,
              capturing fine-grained interactions the fast stage misses. This
              runs only on the top candidates to stay within latency budgets.
            </p>
          </div>
        </div>
      </div>

      {/* AI evidence */}
      <div className="ta-card p-6 mb-8">
        <h2 className="text-lg font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-400" />
          AI Evidence Extraction
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          After ML scoring, a large language model reads both documents and
          extracts bullet-level evidence with confidence weights:
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <EvidenceType
            kind="Strength"
            color="emerald"
            example="Candidate has deep experience in streaming data systems, directly matching the JD's core requirement."
          />
          <EvidenceType
            kind="Gap"
            color="rose"
            example="JD requires on-call rotation experience; resume shows no incident response or production support."
          />
          <EvidenceType
            kind="Keyword"
            color="blue"
            example="Exact technology overlap detected across multiple tools and frameworks in both documents."
          />
          <EvidenceType
            kind="Transfer"
            color="sky"
            example="Clinical research methodology transfers to ML experiment design (statistical rigor, causal inference)."
          />
        </div>
      </div>

      {/* Bias audit */}
      <div className="ta-card p-6 mb-8">
        <h2 className="text-lg font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-400" />
          Bias Audit
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          Every match can be automatically audited for identity-signal influence.
          The PII masking pipeline strips 9 categories of personally identifying
          information, then re-scores the match:
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
          {["Names", "Schools", "Grad years", "Emails", "Phones", "Addresses", "URLs", "Pronouns", "DOBs"].map(
            (cat) => (
              <span
                key={cat}
                className="text-xs text-center py-1.5 px-2 rounded-lg bg-slate-900/50 border border-slate-800/60 text-slate-400"
              >
                {cat}
              </span>
            ),
          )}
        </div>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          The delta between unmasked and masked overall scores quantifies how
          much identity information influenced the match. Significant deltas are
          auto-flagged for human review. The bias audit PDF is a filing-ready
          compliance artifact with per-signal breakdowns and redaction counts.
        </p>
        <div className="grid sm:grid-cols-4 gap-3">
          <SeverityBadge severity="None" desc="Negligible influence" color="emerald" />
          <SeverityBadge severity="Mild" desc="Within expected noise" color="sky" />
          <SeverityBadge severity="Moderate" desc="Flagged for review" color="amber" />
          <SeverityBadge severity="Severe" desc="Requires human review" color="rose" />
        </div>
      </div>

      {/* Benchmark validation */}
      <div className="ta-card p-6 mb-8">
        <h2 className="text-lg font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Target className="w-5 h-5 text-amber-400" />
          Benchmark Validation
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          Marketing claims are cheap. Our scoring engine is regression-gated in
          CI against a labeled benchmark dataset with human-annotated ground truth.
          Every code change is measured — the build fails if ranking quality drops.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard
            label="nDCG@5"
            value={BENCHMARK_SUMMARY.metrics.ndcgAt5.toFixed(2)}
            tooltip="Normalized discounted cumulative gain — measures ranking quality"
          />
          <MetricCard
            label="Precision@1"
            value={BENCHMARK_SUMMARY.metrics.precisionAt1.toFixed(2)}
            tooltip="How often the top-ranked candidate is the best match"
          />
          <MetricCard
            label="MRR"
            value={BENCHMARK_SUMMARY.metrics.mrr.toFixed(2)}
            tooltip="Mean reciprocal rank — how quickly the best match appears"
          />
          <MetricCard
            label="Spearman"
            value={BENCHMARK_SUMMARY.metrics.spearman.toFixed(2)}
            tooltip="Rank correlation between predicted and human rankings"
          />
        </div>
        <div className="mt-3 text-xs text-slate-600 text-center">
          {BENCHMARK_SUMMARY.queryCount} queries,{" "}
          {BENCHMARK_SUMMARY.matchCount} matches | CI-gated regression floor
        </div>
      </div>

      {/* Fusion philosophy */}
      <div className="ta-card p-6">
        <h2 className="text-lg font-bold text-slate-100 mb-3">
          Scoring Philosophy
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">
          The seven dimension scores are combined via a proprietary weighted
          fusion calibrated against human-labeled data. The weights are not
          equal — dimensions that correlate most strongly with recruiter
          decisions carry more influence, while noisier signals are damped.
        </p>
        <div className="space-y-3">
          <PhilosophyPoint
            title="Content over identity"
            desc="Skills, experience, and responsibilities drive the score. Names, schools, and demographics do not."
          />
          <PhilosophyPoint
            title="Specificity rewarded"
            desc="Exact bullet-level matches and multi-word technical phrases count more than vague keyword overlap."
          />
          <PhilosophyPoint
            title="Transfer credit"
            desc="Career changers with genuinely transferable skills receive partial credit instead of being rejected outright."
          />
          <PhilosophyPoint
            title="Honest verdicts"
            desc="The engine says no when it should. A PM applying for a backend engineering role scores low — this is a feature, not a bug."
          />
        </div>
      </div>
    </section>
  );
}

function PipelineStep({
  num,
  title,
  desc,
}: {
  num: string;
  title: string;
  desc: string;
}) {
  return (
    <div className="text-center">
      <div className="text-2xl font-extrabold ta-gradient-text mb-1">{num}</div>
      <div className="text-sm font-semibold text-slate-100 mb-1">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function SignalCard({
  icon: Icon,
  name,
  color,
  description,
}: {
  icon: typeof Brain;
  name: string;
  color: string;
  description: string;
}) {
  const colorMap: Record<string, string> = {
    blue: "text-blue-400",
    purple: "text-purple-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    sky: "text-sky-400",
    violet: "text-violet-400",
    rose: "text-rose-400",
  };
  return (
    <div className="ta-card p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${colorMap[color] ?? "text-slate-400"}`} />
        <h3 className="text-sm font-semibold text-slate-100">{name}</h3>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed">{description}</p>
    </div>
  );
}

function EvidenceType({
  kind,
  color,
  example,
}: {
  kind: string;
  color: string;
  example: string;
}) {
  const colorMap: Record<string, string> = {
    emerald: "border-emerald-500/30 text-emerald-300",
    rose: "border-rose-500/30 text-rose-300",
    blue: "border-blue-500/30 text-blue-300",
    sky: "border-sky-500/30 text-sky-300",
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color]}`}>
      <div className="text-xs font-semibold mb-1">{kind}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{example}</div>
    </div>
  );
}

function SeverityBadge({
  severity,
  desc,
  color,
}: {
  severity: string;
  desc: string;
  color: string;
}) {
  const colorMap: Record<string, string> = {
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    sky: "bg-sky-500/10 border-sky-500/30 text-sky-300",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
    rose: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  };
  return (
    <div className={`text-center py-2 rounded-lg border ${colorMap[color]}`}>
      <div className="text-xs font-semibold">{severity}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{desc}</div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  tooltip: string;
}) {
  return (
    <div className="ta-card p-3 text-center" title={tooltip}>
      <div className="text-lg font-bold ta-gradient-text">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
        {label}
      </div>
    </div>
  );
}

function PhilosophyPoint({
  title,
  desc,
}: {
  title: string;
  desc: string;
}) {
  return (
    <div className="flex gap-3">
      <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
      <div>
        <span className="text-sm text-slate-200 font-medium">{title}</span>
        <span className="text-sm text-slate-400"> — {desc}</span>
      </div>
    </div>
  );
}
